import { useTranslation } from 'react-i18next';
import { ComponentProps, useContext } from 'react';
import { gql, useQuery } from '@apollo/client';
import _, { groupBy } from 'lodash';
import {
  Typography,
  Box,
  Skeleton,
} from '../../../..';
import { translateBackend } from '../../../../../assets/i18n/config';
import { WorkflowContext } from '../../workflowCompletion';
import { Account, AccountStates } from '../../../../../interfaces';
import { useInnerWorkflow, WorkflowCompletionWithId } from '../../components/useInnerWorkflow';
import { useGlobalToast } from '../../../../../providers/globalToastProvider';
import { AccountCard } from './accountCard';

export type ReviewAccountSubstepOptions = {
  title?: {
    en?: string
    fr?: string
  },
  subtitle?: {
    en?: string
    fr?: string
  },
  createAnotherAccount?: boolean,
  enableAccountEdit?: boolean,
  enableAccountDelete?: boolean,
  accountEditWorkflow?: 'default' | string,
  displayAccountType?: boolean,
  displayCustodian?: boolean,
  displayRepcode?: boolean,
  displayRiskTolerence?: boolean,
  displayAffiliates?: boolean,
  displayLinkedGoal?: boolean,
  displayCustomFields?: boolean,
  enableCallToActions?: boolean,
  callToActions?: unknown[]
};

const FETCH_ACCOUNTS = gql`
  query fetchAccountsForReview($input: FetchAccountsInput!) {
    fetchAccounts(input: $input) {
      accounts {
        id
        state
        type
        custodianConnection {
          name
        }
        repCode {
          name
        }
        affiliations {
          type
          relation
          user {
            firstName
            middleName
            lastName
          }
        }
        subAccounts {
          id
          goal {
            name
          }
        }
        customFields {
          key
          value
          customField {
            type
            selectOptions {
              displayText { en fr }
              value
            }
            translatedName {
              en
              fr
            }
          }
        }
      }
    }
  }
`;

export const ReviewAccountVisual = ({
  options,
  userId,
  workflowCompletion,
}: {
  options: ReviewAccountSubstepOptions,
  userId?: string,
  workflowCompletion: WorkflowCompletionWithId,
}) => {
  const { workflowData } = useContext(WorkflowContext);
  const { data, loading, refetch } = useQuery<{ fetchAccounts: { accounts: Account[] } }>(
    FETCH_ACCOUNTS,
    {
      variables: {
        input: {
          pagination: { perPage: 100 },
          filter: {
            userId: workflowData.currentUserId || userId,
            states: [
              AccountStates.ACTIVE,
              AccountStates.REQUESTED,
              AccountStates.FROZEN,
              AccountStates.INITIATED,
              AccountStates.READY,
            ],
          },
        },
      },
    },
  );

  const { t } = useTranslation(['workflowCompletions', 'accountTypes', 'affiliationTypes']);

  const { createWorkflowCompletion, innerWorkflowModal } = useInnerWorkflow({ refetch, workflowCompletion });
  const currentUserId = workflowData.currentUserId || userId;
  const { showToast } = useGlobalToast();
  const onAccountEdit: ComponentProps<typeof AccountCard>['onAccountEdit'] = async (accountId) => {
    if (!options.accountEditWorkflow || options.accountEditWorkflow === 'default') {
      showToast({
        message: t('reviewAccount.noWorkflowErrorMessage'),
        severity: 'error',
      });
      return;
    }
    await createWorkflowCompletion({
      currentUserId,
      workflowId: options.accountEditWorkflow,
      workflowContext: {
        ...workflowData,
        currentAccountId: accountId,
      },
    });
  };

  const accountsById = groupBy(data?.fetchAccounts?.accounts, 'id');

  const accountsFromWorkflow = _([
    workflowData?.currentAccountId,
    ...workflowData?.createdAccounts?.map((a) => a.id) || [],
  ])
    .uniq()
    .map((id) => accountsById[id || '']?.[0])
    .filter((account) => !!account)
    .value();

  const accountsToDisplay = accountsFromWorkflow.length ? accountsFromWorkflow : (data?.fetchAccounts?.accounts ?? []);

  return (
    <Box>
      <Typography variant='displayLarge' sx={{ mt: 1 }}>
        {translateBackend(options?.title)}
      </Typography>
      <Typography variant='bodyLarge' sx={{ mb: 3 }}>
        {translateBackend(options?.subtitle)}
      </Typography>

      {loading ? (
        <>
          <Box sx={{ mb: 2 }} display='flex' justifyContent='space-between'>
            <Skeleton width={250} />
            <Skeleton width={150} />
          </Box>
          <Box sx={{ mb: 2 }} display='flex' justifyContent='space-between'>
            <Skeleton width={250} />
            <Skeleton width={150} />
          </Box>
          <Box sx={{ mb: 2 }} display='flex' justifyContent='space-between'>
            <Skeleton width={250} />
            <Skeleton width={150} />
          </Box>
        </>
      ) : (
        accountsToDisplay?.map((account) => (
          <AccountCard
            key={account.id}
            account={account}
            options={options}
            onAccountDeleted={() => refetch()}
            onAccountEdit={onAccountEdit}
          />
        ))
      )}
      {innerWorkflowModal}
    </Box>
  );
};
